import { useEffect } from 'react';
import { useLocation, useSearchParams, useParams } from 'react-router-dom';

/**
 * Hook to track and preserve company code across navigation
 * Extracts company code from URL path parameter or query string and stores it in sessionStorage
 */
export function useCompanyCode() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();

  useEffect(() => {
    // Check for code in URL path parameters (e.g., /perks-directory/:code)
    let companyCode = params.code;

    // If not in path, check query params
    if (!companyCode) {
      companyCode = searchParams.get('company_code');
    }

    // If not in query, check if it's the last segment of the path
    // (for patterns like /perks-directory/gh5r1bdq)
    if (!companyCode) {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      if (pathSegments.length >= 2 && pathSegments[0] === 'perks-directory') {
        companyCode = pathSegments[1];
      }
    }

    if (companyCode) {
      // Store in sessionStorage for persistence across navigation
      sessionStorage.setItem('company_code', companyCode);
      // Also store as signup code for compatibility with existing code
      sessionStorage.setItem('company_signup_code', companyCode);
    }
  }, [location, searchParams, params]);

  // Return the current company code
  const getCompanyCode = () => {
    return sessionStorage.getItem('company_code') || sessionStorage.getItem('company_signup_code');
  };

  return { companyCode: getCompanyCode() };
}

/**
 * Utility function to append company code to a URL path
 * Appends as path segment for perks-directory, as query param for other paths
 * @param {string} path - The path to navigate to
 * @returns {string} - Path with company_code appended if available
 */
export function appendCompanyCode(path) {
  const companyCode = sessionStorage.getItem('company_code') || sessionStorage.getItem('company_signup_code');

  if (!companyCode) return path;

  // Check if company_code already exists in the path
  if (path.includes(`/${companyCode}`) || path.includes(`company_code=${companyCode}`)) {
    return path;
  }

  // For perks-directory, append as path segment
  if (path === '/perks-directory' || path.startsWith('/perks-directory?')) {
    const [basePath, queryString] = path.split('?');
    const newPath = `${basePath}/${companyCode}`;
    return queryString ? `${newPath}?${queryString}` : newPath;
  }

  // For other paths, append as query parameter
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}company_code=${companyCode}`;
}

/**
 * Utility function to create a navigate function that preserves company code
 * @param {function} navigate - The useNavigate hook function
 * @returns {function} - Enhanced navigate function that preserves company code
 */
export function createCompanyNavigate(navigate) {
  return (path, options) => {
    const enhancedPath = appendCompanyCode(path);
    return navigate(enhancedPath, options);
  };
}
